import { createSlice, type PayloadAction } from "@reduxjs/toolkit";
import type { PortalAttendanceTodayResponse } from "@/actions/ssr/attendance/portal/getPortalAttendanceTodaySSR";
import type { DropRequestsResponse } from "@/actions/ssr/scheduling/getDropRequestsSSR";
import type { MySchedulingAssignmentsResponse } from "@/actions/ssr/scheduling/getMySchedulingAssignmentsSSR";
import type { SwapRequestsResponse } from "@/actions/ssr/scheduling/getSwapRequestsSSR";
import type { TargetAssignmentsKpi } from "@/actions/ssr/target/getTargetAssignmentsKpiSSR";
import type { TargetAssignmentsResponse } from "@/actions/ssr/target/getTargetAssignmentsSSR";
import type { PortalTargetsRankResponse } from "@/actions/ssr/target/portal/getPortalTargetsRankSSR";

interface PortalDashboardState {
  todayHydrated: boolean;
  today: PortalAttendanceTodayResponse;
  targetKpi: TargetAssignmentsKpi;
  activeTargets: TargetAssignmentsResponse;
  rank: PortalTargetsRankResponse;
  upcomingShiftsHydrated: boolean;
  upcomingShifts: MySchedulingAssignmentsResponse;
  dropRequests: DropRequestsResponse;
  swapRequests: SwapRequestsResponse;
}

const initialState: PortalDashboardState = {
  todayHydrated: false,
  today: {
    status: "off_shift",
    hours_worked_today: "00:00",
    clock_in: null,
    clock_out: null,
    break_start: null,
    break_end: null,
    break_minutes: 0,
    current_session_break_minutes: 0,
    break_is_paid: false,
    hours_worked_today_paid_aware: "00:00",
    location: null,
    is_on_leave: false,
  },
  targetKpi: {
    total_assigned: 0,
    total_assigned_trend_pct: 0,
    active_targets: 0,
    active_near_completion: 0,
    completion_rate_pct: 0,
    completion_rate_trend_pct: 0,
    pending_reviews: 0,
    total_completed: 0,
  },
  activeTargets: {
    count: 0,
    next: null,
    previous: null,
    total_pages: 1,
    current_page: 1,
    results: [],
    sales_target_count: 0,
    visit_count: 0,
    general_count: 0,
    collection_count: 0,
  },
  rank: {
    staff_id: "",
    total_points: 0,
    total_completed: 0,
    total_assigned: 0,
    total_rejected: 0,
    last_awarded_at: null,
    rank: 0,
    rank_period: "all",
    previous_rank: null,
    rank_change: null,
    tier: null,
    badges: [],
  },
  upcomingShiftsHydrated: false,
  upcomingShifts: { count: 0, results: [] },
  dropRequests: {
    next: null,
    previous: null,
    count: 0,
    total_pages: 1,
    current_page: 1,
    results: [],
    kpi: {
      total_count: 0,
      pending_count: 0,
      approved_count: 0,
      rejected_count: 0,
    },
  },
  swapRequests: {
    next: null,
    previous: null,
    count: 0,
    total_pages: 1,
    current_page: 1,
    results: [],
    kpi: {
      total_count: 0,
      pending_count: 0,
      approved_count: 0,
      rejected_count: 0,
    },
  },
};

const portalDashboardSlice = createSlice({
  name: "portalDashboard",
  initialState,
  reducers: {
    setPortalDashboardToday(
      state,
      action: PayloadAction<PortalAttendanceTodayResponse>,
    ) {
      state.today = action.payload;
      state.todayHydrated = true;
    },
    setPortalDashboardTargetKpi(
      state,
      action: PayloadAction<TargetAssignmentsKpi>,
    ) {
      state.targetKpi = action.payload;
    },
    setPortalDashboardActiveTargets(
      state,
      action: PayloadAction<TargetAssignmentsResponse>,
    ) {
      state.activeTargets = action.payload;
    },
    setPortalDashboardRank(
      state,
      action: PayloadAction<PortalTargetsRankResponse>,
    ) {
      state.rank = action.payload;
    },
    setPortalDashboardUpcomingShifts(
      state,
      action: PayloadAction<MySchedulingAssignmentsResponse>,
    ) {
      state.upcomingShifts = action.payload;
      state.upcomingShiftsHydrated = true;
    },
    setPortalDashboardDropRequests(
      state,
      action: PayloadAction<DropRequestsResponse>,
    ) {
      state.dropRequests = action.payload;
    },
    setPortalDashboardSwapRequests(
      state,
      action: PayloadAction<SwapRequestsResponse>,
    ) {
      state.swapRequests = action.payload;
    },
  },
});

export const {
  setPortalDashboardToday,
  setPortalDashboardTargetKpi,
  setPortalDashboardActiveTargets,
  setPortalDashboardRank,
  setPortalDashboardUpcomingShifts,
  setPortalDashboardDropRequests,
  setPortalDashboardSwapRequests,
} = portalDashboardSlice.actions;
export default portalDashboardSlice.reducer;
